import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getExportColumns } from '../../services/shipturtleApi';

const EXPORT_OPTIONS = [
  { id: 'shipping_labels', label: 'Shipping Labels', formats: ['zip', 'pdf'] },
  { id: 'invoices', label: 'Invoices', formats: ['zip', 'pdf'] },
  { id: 'orders_csv', label: 'Export Orders', formats: ['xlsx', 'csv'] },
];

const formatLabel = {
  zip: 'ZIP (separate PDFs)',
  pdf: 'Single PDF (merged)',
  csv: 'CSV (plain text)',
  xlsx: 'Excel (.xlsx)',
};

function ExportOrdersModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  error = '',
  selectedCount = 0,
  filteredCount = 0,
}) {
  const [type, setType] = useState('');
  const [format, setFormat] = useState('');
  // Fetched per role: admins and vendors are entitled to different columns.
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const containerRef = useRef(null);
  // Read by the Enter-to-confirm handler below, which must not re-register
  // on every column toggle.
  const selectedColumnsRef = useRef(selectedColumns);
  selectedColumnsRef.current = selectedColumns;

  useEffect(() => {
    if (!isOpen) return;

    setType('');
    setFormat('');

    let cancelled = false;
    setColumnsLoading(true);
    getExportColumns()
      .then((data) => {
        if (cancelled) return;
        const columns = data?.columns || [];
        setAvailableColumns(columns);
        setSelectedColumns(columns);
      })
      .catch((err) => {
        console.error('Failed to load export columns:', err);
        if (!cancelled) {
          setAvailableColumns([]);
          setSelectedColumns([]);
        }
      })
      .finally(() => {
        if (!cancelled) setColumnsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key === 'Enter' && !isSubmitting && type && format) {
        event.preventDefault();
        const columns = type === 'orders_csv' ? selectedColumnsRef.current : undefined;
        onConfirm({ type, format, columns });
      }

      if (event.key !== 'Tab') return;
      const focusable = containerRef.current?.querySelectorAll(focusableSelectors);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    containerRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused) previouslyFocused.focus();
    };
  }, [isOpen, type, format, isSubmitting, onConfirm, onClose]);

  const handleTypeChange = (value) => {
    setType(value);
    const defaultFormat = EXPORT_OPTIONS.find((opt) => opt.id === value)?.formats[0] || '';
    setFormat(defaultFormat);
  };

  const handleConfirm = () => {
    if (!type || !format || isSubmitting) return;
    const columns = type === 'orders_csv' ? selectedColumns : undefined;
    onConfirm({ type, format, columns });
  };

  const toggleColumn = (col) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  if (!isOpen) {
    return null;
  }

  const allowAllNotice =
    selectedCount === 0
      ? 'No orders selected — the export will include all orders in the current filtered view.'
      : `${selectedCount} order(s) selected.`;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          zIndex: 1000000,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: '#111827' }}>
            Export Orders
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close export modal"
          >
            ×
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          <p style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '14px' }}>
            Choose what you want to export. Large jobs (over 20 line items) may be emailed.
          </p>
          <p style={{ margin: '0 0 24px 0', padding: '12px 16px', background: '#dbeafe', borderLeft: '4px solid #3b82f6', borderRadius: '4px', color: '#1e293b', fontSize: '13px' }}>
            {allowAllNotice} Showing {filteredCount} order(s) in this view.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
            {EXPORT_OPTIONS.map((option) => (
              <div
                key={option.id}
                style={{
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '16px',
                  transition: 'all 0.2s',
                  background: '#fff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="exportType"
                    value={option.id}
                    checked={type === option.id}
                    onChange={() => handleTypeChange(option.id)}
                    style={{ margin: '4px 12px 0 0', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '15px', color: '#111827', marginBottom: '12px' }}>
                      {option.label}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {option.formats.map((fmt) => {
                        const isChecked = type === option.id && format === fmt;
                        const isDisabled = type !== option.id;
                        return (
                          <label
                            key={fmt}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '6px 12px',
                              border: `1px solid ${isChecked ? '#3b82f6' : '#9ca3af'}`,
                              borderRadius: '20px',
                              background: isChecked ? '#3b82f6' : '#f3f4f6',
                              color: isChecked ? '#fff' : '#374151',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              opacity: isDisabled ? 0.5 : 1,
                              fontSize: '13px',
                            }}
                          >
                            <input
                              type="radio"
                              name={`format-${option.id}`}
                              value={fmt}
                              disabled={isDisabled}
                              checked={isChecked}
                              onChange={() => setFormat(fmt)}
                              style={{ display: 'none' }}
                            />
                            <span>{formatLabel[fmt]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          {/* Column picker — visible only when orders_csv is selected */}
          {type === 'orders_csv' && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                  Select Columns ({selectedColumns.length}/{availableColumns.length})
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedColumns(availableColumns)}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      background: '#fff',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedColumns([])}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #ef4444',
                      borderRadius: '4px',
                      background: '#fff',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px 12px' }}>
                {columnsLoading && (
                  <div style={{ padding: '8px 0', fontSize: '13px', color: '#6b7280' }}>Loading columns…</div>
                )}
                {!columnsLoading && availableColumns.length === 0 && (
                  <div style={{ padding: '8px 0', fontSize: '13px', color: '#6b7280' }}>
                    Could not load the column list. The export will include all columns available to you.
                  </div>
                )}
                {availableColumns.map((col) => (
                  <label key={col} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(col)}
                      onChange={() => toggleColumn(col)}
                      style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                    />
                    {col}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ padding: '12px 16px', background: '#fef3c7', borderLeft: '4px solid #f59e0b', borderRadius: '4px', color: '#92400e', fontSize: '13px', marginBottom: '20px' }}>
            📧 Exports are generated in the background and emailed to you when ready. Large exports can take a few minutes.
          </div>

          {error && (
            <div style={{ padding: '12px 16px', background: '#fee2e2', borderLeft: '4px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '20px' }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: '20px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              border: '2px solid #9ca3af',
              borderRadius: '6px',
              background: '#fff',
              color: '#374151',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!type || !format || isSubmitting}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              background: (!type || !format || isSubmitting) ? '#9ca3af' : '#3b82f6',
              color: '#fff',
              cursor: (!type || !format || isSubmitting) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              opacity: (!type || !format || isSubmitting) ? 0.6 : 1,
            }}
          >
            {isSubmitting ? 'Preparing…' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ExportOrdersModal;
